import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Phone, Home } from "lucide-react";

export default function ThankYouPage() {
  useEffect(() => {
    const fireLead = () => {
      if (window.fbq && window.fbq.callMethod) {
        window.fbq('track', 'Lead');
        return true;
      }
      return false;
    };
    if (!fireLead()) {
      const interval = setInterval(() => {
        if (fireLead()) clearInterval(interval);
      }, 500);
      const timeout = setTimeout(() => clearInterval(interval), 5000);
      return () => { clearInterval(interval); clearTimeout(timeout); };
    }
  }, []);

  return (
    <div data-testid="thank-you-page" className="min-h-screen bg-[#0A0A0A] flex items-center justify-center pt-20 pb-20">
      {/* Meta Pixel Lead Event - img fallback */}
      <img height="1" width="1" style={{ display: "none" }} alt=""
        src="https://www.facebook.com/tr?id=866371716194262&ev=Lead&noscript=1"
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          {/* Icon */}
          <div className="w-20 h-20 bg-[#FF5722] flex items-center justify-center mx-auto mb-8">
            <CheckCircle size={40} className="text-white" />
          </div>

          {/* Heading */}
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4"
            style={{ fontFamily: "'Clash Display', sans-serif" }}
          >
            Thank You!
          </h1>
          <p className="text-base sm:text-lg text-[#A3A3A3] mb-4 max-w-lg mx-auto leading-relaxed">
            Your quote request has been received. Our engineering team will review your specifications and prepare a detailed, no-obligation quote.
          </p>
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-[#141414] border border-[#2E2E2E] p-6 sm:p-8 mb-10 max-w-md mx-auto"
        >
          <h3
            className="text-sm font-bold text-white uppercase tracking-wider mb-5"
            style={{ fontFamily: "'Clash Display', sans-serif" }}
          >
            What Happens Next
          </h3>
          <div className="space-y-4 text-left">
            {[
              { step: "1", text: "Our team reviews your project specifications" },
              { step: "2", text: "We prepare a tailored quote with pricing" },
              { step: "3", text: "You'll receive your quote within 24 business hours" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4">
                <div className="w-7 h-7 bg-[#FF5722]/10 flex items-center justify-center shrink-0">
                  <span className="text-[#FF5722] text-xs font-bold">{item.step}</span>
                </div>
                <p className="text-sm text-[#A3A3A3] pt-0.5">{item.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/quote"
            data-testid="submit-another-quote-btn"
            className="inline-flex items-center gap-2 bg-[#FF5722] text-white px-6 py-3 font-semibold text-sm hover:bg-[#E64A19] transition-colors"
          >
            Submit Another Quote
            <ArrowRight size={16} />
          </Link>
          <Link
            to="/"
            data-testid="back-to-home-btn"
            className="inline-flex items-center gap-2 border border-[#2E2E2E] text-[#A3A3A3] px-6 py-3 font-semibold text-sm hover:border-[#FF5722] hover:text-white transition-all"
          >
            <Home size={16} />
            Back to Home
          </Link>
        </motion.div>

        {/* Contact fallback */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-12 pt-8 border-t border-[#2E2E2E]"
        >
          <p className="text-sm text-[#A3A3A3] mb-3">Need to speak with us urgently?</p>
          <a
            href="tel:+61413136160"
            data-testid="thank-you-phone-link"
            className="inline-flex items-center gap-2 text-[#FF5722] font-semibold text-sm hover:text-[#E64A19] transition-colors"
          >
            <Phone size={16} />
            +61 413 136 160
          </a>
        </motion.div>
      </div>
    </div>
  );
}
